import { Prices } from "../utils/models/cart";

export const BROCHURE_BASKETS_BASE = '/apps/errea-b2b';

export const createBrochure = (name: string, company: string, productSkus: string[]): Promise<BrochureBasket> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ name, company, product_skus: productSkus }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export interface BrochureBasket {
    id: number;
    name: string;
    company: string;
    products_count: number;
    created_at: string;
    updated_at: string;
}

export interface BrochuresResponse {
    data: BrochureBasket[];
    links: {
        first: string | null;
        last: string | null;
        prev: string | null;
        next: string | null;
    };
    meta: {
        current_page: number;
        from: number;
        path: string;
        per_page: string;
        to: number;
    };
}

export interface BrochureProduct {
    id: number;
    sku: string;
    ecommerce_id: string;
    name: string;
    color: string;
    images: string[];
    is_configurable: boolean;
    is_ready_to_hot: boolean;
    custom_products?: CustomProduct[];
}

export interface BrochureItem {
    id: number;
    name: string;
    last_modified?: string;
    updated_at?: string;
    products_count?: number;
}

export interface BrochureBasketDetail {
    id: number;
    name: string;
    company: string;
    products_count: number;
    products: BrochureProduct[];
    brochures: BrochureItem[];
    created_at: string;
    updated_at: string;
}

export const getBrochureBasketById = (id: string, lang: string): Promise<BrochureBasketDetail> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${id}?lang=${lang}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const deleteProductFromBrochureBasket = (basketId: string, productId: number): Promise<void> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'DELETE',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/products/${productId}`, requestOptions)
            .then((response) => {
                if (response.ok) {
                    resolve();
                } else {
                    return response.json().then((result) => reject(result.error ?? 'error'));
                }
            })
            .catch((error) => reject(error));
    });
};

export const deleteCustomProductFromBrochureBasket = (
    basketId: string,
    productId: number,
    customProductId: number,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'DELETE',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(
            `${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/products/${productId}/custom-products/${customProductId}`,
            requestOptions,
        )
            .then((response) => {
                if (response.ok) {
                    resolve();
                } else {
                    return response.json().then((result) => reject(result.error ?? 'error'));
                }
            })
            .catch((error) => reject(error));
    });
};

export const deleteBrochureBasket = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'DELETE',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${id}`, requestOptions)
            .then((response) => {
                if (response.ok) {
                    resolve();
                } else {
                    return response.json().then((result) => reject(result.error ?? 'error'));
                }
            })
            .catch((error) => reject(error));
    });
};

export interface BrochureTemplate {
    id: number;
    name: string;
    slug: string;
    preview_image: string;
    max_products: number;
}

export interface CreateBrochureProductPayload {
    id: number;
    position: number;
    with_card_side: boolean;
    image_preset?: string;
    custom_product_id?: number;
}

export interface CreateBrochurePagePayload {
    template_id: number;
    title: string;
    products: CreateBrochureProductPayload[];
}

export interface CreateBrochurePayload {
    name: string;
    pages: CreateBrochurePagePayload[];
}

export interface BrochureDetailProduct {
    id: number;
    position: number;
    custom_product?: CustomProduct;
    with_card_side: boolean;
    image_preset?: string;
}

export interface ZakekeCameraScreenshot {
    url: string;
    sideName: string;
    cameraName: string;
}

export interface CustomProduct {
    id: number;
    product_id: number;
    zakeke_design_id: string;
    zakeke_preview_image: string;
    zakeke_front_area_colors?: string;
    created_at: string;
    updated_at: string;
}

export interface OrderProductVariant {
    ecommerce_id: string,
    sku: string,
    option1: string | null,
    option2: string | null,
    option3: string | null,
    deliveryPeriod: BrochureOrderProduct['delivery_period'], // Redundant but keeps the info in the variant for less lookups when validating building
    pfsDeliveryItem: BrochureOrderProduct['pfs_item_status'], // Redundant but keeps the info in the variant for less lookups when validating building
    fetchedPrices?: Prices; // Added post fetch, not part of the API response
    inventoryQuantity?: number; // Added post fetch, not part of the API response
}

interface OrderProductOption {
    name: string;
}

export interface BrochureOrderProduct extends BrochureDetailProduct {
    sku: string,
    vendor: string,
    name: string,
    color: string,
    images: string[],
    category: string,
    options: OrderProductOption[],
    variants: OrderProductVariant[],
    custom_product?: CustomProduct,
    delivery_period: string,
    pfs_item_status: boolean
}

export interface BrochureDetailPage {
    id: number;
    template: BrochureTemplate;
    title: string;
    products: BrochureDetailProduct[];
}

export interface BrochureDetail {
    id: number;
    name: string;
    pages: BrochureDetailPage[];
    products_count: number;
    updated_at: string;
}

export interface BrochureSelectableProduct {
    id: string;
    entityId: number;
    baseProductId: number;
    name: string;
    sku: string;
    color: string;
    image: string;
    isCustomProduct: boolean;
    product: BrochureProduct;
    customProduct?: CustomProduct;
}

export const getBrochureProductImage = (product: BrochureProduct | BrochureOrderProduct, customProduct?: CustomProduct): string => {
    if (customProduct && !!customProduct?.zakeke_preview_image) {
        return customProduct.zakeke_preview_image;
    }

    return product.images[0] ?? ''
};

export const getBrochureSelectableProducts = (products: BrochureProduct[]): BrochureSelectableProduct[] => {
    const selectableProducts: BrochureSelectableProduct[] = [];

    products.forEach((product) => {
        selectableProducts.push({
            id: String(product.id),
            entityId: product.id,
            baseProductId: product.id,
            name: product.name,
            sku: product.sku,
            color: product.color,
            image: getBrochureProductImage(product),
            isCustomProduct: false,
            product,
        });

        product.custom_products?.forEach((customProduct) => {
            selectableProducts.push({
                id: String(customProduct.id),
                entityId: customProduct.id,
                baseProductId: product.id,
                name: product.name,
                sku: product.sku,
                color: product.color,
                image: getBrochureProductImage(product, customProduct),
                isCustomProduct: true,
                product,
                customProduct,
            });
        });
    });

    return selectableProducts;
};

export const getBrochureSelectedProducts = (
    brochure: BrochureDetail | null,
    products: BrochureSelectableProduct[],
): BrochureSelectableProduct[] => {
    if (!brochure) return [];

    const selectedProducts: BrochureSelectableProduct[] = [];
    const seenIds = new Set<string>();

    brochure.pages.forEach((page) => {
        page.products.forEach((pageProduct) => {
            const selectedProductId = String(pageProduct.custom_product?.id ?? pageProduct.id);
            const selectedProduct = products.find((product) => product.id === selectedProductId);

            if (!selectedProduct || seenIds.has(selectedProduct.id)) return;

            seenIds.add(selectedProduct.id);
            selectedProducts.push(selectedProduct);
        });
    });

    return selectedProducts;
};

export const getBrochureById = (basketId: string, brochureId: string): Promise<BrochureDetail> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures/${brochureId}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ?? 'error');
                }
            })
            .catch((error) => reject(error));
    });
};

export const deleteBrochureFromBasket = (basketId: string, brochureId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures/${brochureId}`, {
            method: 'DELETE',
            headers,
            redirect: 'follow' as RequestRedirect,
        })
            .then((response) => {
                if (response.ok) {
                    resolve();
                } else {
                    return response.json().then((result) => reject(result.error ?? 'error'));
                }
            })
            .catch((error) => reject(error));
    });
};

export const createBrochureInBasket = (basketId: string, payload: CreateBrochurePayload): Promise<BrochureDetail> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures`, requestOptions)
            .then(async (response) => {
                const result = await response.json();

                if (response.ok) {
                    resolve(result);
                } else {
                    reject(result.error ?? 'error');
                }
            })
            .catch((error) => reject(error));
    });
};

export const updateBrochureInBasket = (basketId: string, brochureId: string, payload: CreateBrochurePayload): Promise<BrochureDetail> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'error' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures/${brochureId}`, requestOptions)
            .then(async (response) => {
                const result = await response.json();

                if (response.ok) {
                    resolve(result);
                } else {
                    reject(result.error ?? 'error');
                }
            })
            .catch((error) => reject(error));
    });
};

export const getBrochureBasketTemplates = (): Promise<BrochureTemplate[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/templates`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.data ?? []);
                } else {
                    reject(result.error ?? 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const addCustomProduct = (basketId: string, productId: number, zakekeDesignId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ zakeke_design_id: zakekeDesignId }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/products/${productId}/custom-products`, requestOptions)
            .then((response) => {
                if (response.ok) {
                    resolve();
                } else {
                    return response.json().then((result) => reject(result.error ?? 'error'));
                }
            })
            .catch((error) => reject(error));
    });
};

export const getBrochures = (page: number, perPage: number, name?: string, company?: string): Promise<BrochuresResponse> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const params = new URLSearchParams({
            per_page: String(perPage),
            page: String(page),
        });

        if (name) params.append('name', name);
        if (company) params.append('company', company);

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets?${params.toString()}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getOrderProducts = (basketId: string, brochureId: string, lang: string = ""): Promise<BrochureOrderProduct[]> => {
    return new Promise<BrochureOrderProduct[]>((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures/${brochureId}/order-products${lang ? `?lang=${lang}` : ''}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    const orderProducts = result.data as BrochureOrderProduct[];
                    resolve(orderProducts ?? []);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};
